// Signup hub: account-type choice; student & casting-manager cards in a react-slick carousel.
// Mistake: Tailwind gradient classes didn't render; prefer inline linear-gradient for overlays.
import { PAGE_TYPE } from "enums";
import { getLanguageValue } from "locales/i18n";
import React, { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import AuthHeader from "../component/AuthHeader.component";

const slickArrowBase =
  "!flex h-10 w-10 items-center justify-center rounded-full border border-black bg-black text-white shadow-md hover:bg-black z-10 opacity-60 hover:opacity-100 before:!text-white before:!opacity-100";

function SignupPrevArrow(props: { className?: string; style?: React.CSSProperties; onClick?: () => void }) {
  const { className, style, onClick } = props;
  return (
    <button
      type="button"
      className={`${className ?? ""} ${slickArrowBase}`}
      style={{ ...style, left: "12px", right: "auto" }}
      onClick={onClick}
      aria-label={getLanguageValue(PAGE_TYPE.COMMON, "btn.txtPrev")}
    />
  );
}

function SignupNextArrow(props: { className?: string; style?: React.CSSProperties; onClick?: () => void }) {
  const { className, style, onClick } = props;
  return (
    <button
      type="button"
      className={`${className ?? ""} ${slickArrowBase}`}
      style={{ ...style, right: "12px", left: "auto" }}
      onClick={onClick}
      aria-label={getLanguageValue(PAGE_TYPE.COMMON, "btn.txtNext")}
    />
  );
}

const SignupComponent = (props: any) => {
  const { backUrl } = props;
  const navigate = useNavigate();
  const shouldShowKoachCard = process.env.REACT_APP_ENV !== "prod";

  const sliderSettings = useMemo(
    () => ({
      dots: true,
      infinite: true,
      speed: 450,
      slidesToShow: 1,
      slidesToScroll: 1,
      centerMode: true,
      centerPadding: "52px",
      arrows: true,
      autoplay: false,
      adaptiveHeight: false,
      prevArrow: <SignupPrevArrow />,
      nextArrow: <SignupNextArrow />,
      responsive: [
        {
          breakpoint: 640,
          settings: {
            centerPadding: "24px",
          },
        },
      ],
    }),
    []
  );

  return (
    <div className="min-h-screen overflow-x-hidden">
      <AuthHeader backUrl={backUrl} />
      <main className="bg-gray-50 px-4 pb-12 pt-10 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10 md:mb-12">
            <h1 className="md:text-4xl text-2xl font-bold text-gray-900 mb-6 md:mb-8">
              {getLanguageValue(PAGE_TYPE.COMMON, "signup.txtTitle")}
            </h1>
          </div>

          <div className="signup-cards-carousel pb-4 [&_.slick-dots]:bottom-[-2.25rem] [&_.slick-dots_li.slick-active_button:before]:!text-[#CF3688] [&_.slick-dots_li_button:before]:!text-gray-300 [&_.slick-list]:overflow-hidden [&_.slick-list]:h-[320px] [&_.slick-track]:h-full [&_.slick-track]:flex [&_.slick-slide]:h-full [&_.slick-slide>div]:h-full">
            <Slider {...sliderSettings}>
              <div className="px-1 sm:px-3 outline-none h-full">
                <Link
                  to="/signup/student"
                  className="group relative block max-w-xl mx-auto rounded-xl shadow-lg h-full hover:shadow-xl transition-shadow duration-300 transform hover:-translate-y-1 overflow-hidden"
                >
                  <img
                    src="/imgs/common/signup/signup-student-bg.png"
                    alt="Student signup"
                    className="relative z-0 block h-full w-full object-cover"
                  />
                  <div
                    className="pointer-events-none absolute inset-0 z-10"
                    style={{
                      backgroundImage: "linear-gradient(to bottom, rgba(0,0,0,0) 45%, rgba(0,0,0,0.65) 100%)",
                    }}
                  />
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 p-6 sm:p-8">
                    <p className="text-2xl font-bold text-white sm:text-3xl">
                      {getLanguageValue(PAGE_TYPE.COMMON, "signup.txtAspirantSectionTitle")}
                    </p>
                    <p className="mt-2 text-xs font-medium text-white/90 sm:text-sm">
                      {getLanguageValue(PAGE_TYPE.COMMON, "signup.txtAspirantSectionDesc")}
                    </p>
                  </div>
                </Link>
              </div>
              {shouldShowKoachCard && (
                <div className="px-1 sm:px-3 outline-none h-full">
                  <Link
                    to="/signup/koach"
                    className="group relative block max-w-xl mx-auto rounded-xl shadow-lg h-full hover:shadow-xl transition-shadow duration-300 transform hover:-translate-y-1 overflow-hidden"
                  >
                    <img
                      src="/imgs/common/signup/signup-koach-bg.png"
                      alt="Koach signup"
                      className="relative z-0 block h-full w-full object-cover"
                    />
                    <div
                      className="pointer-events-none absolute inset-0 z-10"
                      style={{
                        backgroundImage:
                          "linear-gradient(to bottom, rgba(0,0,0,0) 45%, rgba(0,0,0,0.65) 100%)",
                      }}
                    />
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 p-6 sm:p-8">
                      <p className="text-2xl font-bold text-white sm:text-3xl">Koach</p>
                      <p className="mt-2 text-xs font-medium text-white/90 sm:text-sm">
                        {getLanguageValue(PAGE_TYPE.COMMON, "signup.txtKoachCardDesc")}
                      </p>
                    </div>
                  </Link>
                </div>
              )}
              <div className="px-1 sm:px-3 outline-none h-full">
                <Link
                  to="/signup/agency"
                  className="group relative block max-w-xl mx-auto rounded-xl shadow-lg h-full hover:shadow-xl transition-shadow duration-300 transform hover:-translate-y-1 overflow-hidden"
                >
                  <img
                    src="/imgs/common/signup/signup-agency-bg.png"
                    alt="Agency signup"
                    className="relative z-0 block h-full w-full object-cover"
                  />
                  <div
                    className="pointer-events-none absolute inset-0 z-10"
                    style={{
                      backgroundImage: "linear-gradient(to bottom, rgba(0,0,0,0) 45%, rgba(0,0,0,0.65) 100%)",
                    }}
                  />
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 z-20 p-6 sm:p-8">
                    <p className="text-2xl font-bold text-white sm:text-3xl">
                      {getLanguageValue(PAGE_TYPE.COMMON, "signup.txtCastingManagerSectionTitle")}
                    </p>
                    <p className="mt-2 text-xs font-medium text-white/90 sm:text-sm">
                      {getLanguageValue(PAGE_TYPE.COMMON, "signup.txtCastingManagerSectionDesc")}
                    </p>
                  </div>
                </Link>
              </div>
            </Slider>
          </div>
        </div>
        <div className="max-w-4xl mx-auto mt-16 border-t border-gray-200 pt-6 text-center">
          <p className="text-sm sm:text-base text-gray-600">
            {getLanguageValue(PAGE_TYPE.COMMON, "signup.txtAlreadyHaveAccount")}
            <br />
            <button
              type="button"
              onClick={() => navigate("/login")}
              className="font-semibold text-[#CF3688] hover:text-[#A62B6D] transition-colors duration-200"
            >
              {getLanguageValue(PAGE_TYPE.COMMON, "signup.btnGoToLogin")}
            </button>
          </p>
        </div>
      </main>
    </div>
  );
};

export default SignupComponent;
